/**
 * Wheel Share Video — pure client logic (SDD-23 / WL-046).
 *
 * The deterministic, framework-free core the modal renders around: the UI state
 * machine (idle → generating → preview | error), the aspect-ratio option set
 * (must mirror the backend AspectRatio enum), the request-payload builder, and
 * the response parser/validator. No React, no network, no DOM here — so it is
 * exhaustively unit-testable and the component stays a thin presentational shell.
 */
import { presetToParams } from "./presets";
import { customizeToParams } from "./customize";
import { buildAttributionPayload } from "./attribution";
import { buildEndCardPayload } from "./endCard";

// ── UI states ────────────────────────────────────────────────────────────────
export const ShareVideoState = Object.freeze({
  IDLE: "idle", // showing options + "Generate" CTA
  GENERATING: "generating", // request in flight (spinner, disabled controls)
  PREVIEW: "preview", // video ready, showing player + share/download
  ERROR: "error", // generation failed, showing retry
});

/**
 * Aspect-ratio options. `value` MUST equal the backend AspectRatio enum values
 * (share_video_service.AspectRatio) — 9:16 / 1:1 / 16:9 — or the API rejects it.
 * Vertical is the default (spec: vertical-first, sound-off-friendly).
 */
export const ASPECT_OPTIONS = Object.freeze([
  { value: "9:16", label: "Vertical", hint: "TikTok · Reels · Stories", w: 9, h: 16 },
  { value: "1:1", label: "Square", hint: "Feed", w: 1, h: 1 },
  { value: "4:5", label: "Portrait", hint: "IG feed", w: 4, h: 5 },
  { value: "16:9", label: "Landscape", hint: "YouTube", w: 16, h: 9 },
]);

export const DEFAULT_ASPECT = "9:16";

export const isValidAspect = (value) =>
  ASPECT_OPTIONS.some((o) => o.value === value);

/**
 * A wheel id is usable for the generate-share-video request only when it is a
 * real, present id. Guards against the `/api/v2/wheel/undefined/…` 404 class:
 * a template literal turns a missing id into the literal strings "undefined" /
 * "null" / "NaN" / "" — none of which are a routable id. Accepts positive
 * numeric ids and non-empty string ids; rejects everything else. Keeps the
 * URL-building path from ever forming a bogus route.
 */
export const isValidWheelId = (id) => {
  if (id === null || id === undefined) return false;
  if (typeof id === "number") return Number.isFinite(id) && id > 0;
  const s = String(id).trim();
  if (!s) return false;
  return s !== "undefined" && s !== "null" && s !== "NaN";
};

/**
 * User-facing message when Share-as-Video can't run because the wheel has no
 * usable id yet (empty/default wheel whose screenshot/preview failed). Shown in
 * the ERROR state instead of a raw network 404.
 */
export const MISSING_WHEEL_MESSAGE =
  "Couldn't capture this wheel — add some content and try again.";

/**
 * Next state given the current state and an event. A tiny explicit reducer so
 * illegal transitions (e.g. success while idle) can't silently corrupt the UI.
 * Unknown transitions are a no-op (return the current state) rather than throw.
 */
export function nextState(current, event) {
  switch (current) {
    case ShareVideoState.IDLE:
      return event === "generate" ? ShareVideoState.GENERATING : current;
    case ShareVideoState.GENERATING:
      if (event === "success") return ShareVideoState.PREVIEW;
      if (event === "error") return ShareVideoState.ERROR;
      if (event === "cancel") return ShareVideoState.IDLE;
      return current;
    case ShareVideoState.PREVIEW:
      // "regenerate" (tweak options + go again) or reset back to idle.
      return event === "regenerate" ? ShareVideoState.GENERATING : current;
    case ShareVideoState.ERROR:
      if (event === "retry") return ShareVideoState.GENERATING;
      if (event === "reset") return ShareVideoState.IDLE;
      return current;
    default:
      return current;
  }
}

export const isBusy = (state) => state === ShareVideoState.GENERATING;

/** True for a base64 PNG/JPEG/WebP data URL — the only `still_image` the BE accepts. */
export const isStillImageDataUrl = (v) =>
  typeof v === "string" && /^data:image\/(png|jpe?g|webp);base64,./.test(v);

/**
 * Build the POST body for /generate-share-video[-async].
 *
 * Always carries `aspect_ratio`. Precedence (SPEC §5.3 — Customize overrides the
 * preset):
 *   1. `customize` (C5) — when it yields a non-empty ordered selection, emit an
 *      explicit `segment_ids` (ordered) + a clamped `target_duration_ms`, and NO
 *      `preset` / `segment_count`. The explicit list IS the selection.
 *   2. `preset` (C4) — otherwise merge the preset's render params (`preset` +
 *      optionally `segment_count` + `target_duration_ms` — see ./presets).
 *   3. neither — the body is exactly `{ aspect_ratio }`, byte-identical to the
 *      pre-C4 contract, so callers that select nothing are unaffected.
 *
 * An empty/absent `customize` (nothing selected) falls THROUGH to the preset
 * path unchanged, so a defensive null can never emit a bogus empty selection.
 *
 * C10: when a creator credit and/or a credit-required music track apply, an
 * `attribution` block is added so a later render (C11) can bake the end-card
 * credit. It carries ONLY what will actually show (privacy-gated creator, verbatim
 * music credit) and is OMITTED entirely when there is nothing to credit — so the
 * body stays byte-identical to today for a private Wheel with no music.
 *
 * C11: when an `endCard` config is supplied ({ ctaText, link, qr, defaultLink }),
 * a `cta` block (SPEC §8.2) is added so a later render bakes the mandatory
 * end-card (CTA line + link + optional QR). It is OMITTED entirely when `endCard`
 * is absent, so a caller that doesn't drive the end-card posts a body
 * byte-identical to today (the flag-off path never passes `endCard`).
 *
 * T22.653: `stillImage` is the Wheel exactly as the Share dialog draws it (a
 * `data:image/...;base64,` URL). The still render bakes it instead of the saved
 * `wheel_preview` screenshot, which is never refreshed after a colour edit (the
 * video showed the old colours). Omitted unless it is a real image data URL.
 */
export function buildRequestPayload({
  aspectRatio = DEFAULT_ASPECT,
  preset,
  customize,
  creator,
  musicTrack,
  endCard,
  stillImage,
} = {}) {
  const ar = isValidAspect(aspectRatio) ? aspectRatio : DEFAULT_ASPECT;
  const payload = { aspect_ratio: ar };
  if (isStillImageDataUrl(stillImage)) payload.still_image = stillImage;

  // Attribution + end-card are independent of preset-vs-customize, so assemble
  // them up front and attach below whichever selection path we take.
  const attribution = buildAttributionPayload({ creator, musicTrack });
  const cta = endCard ? buildEndCardPayload(endCard) : undefined;

  if (customize) {
    const params = customizeToParams({ ...customize, aspect: ar });
    if (params) {
      Object.assign(payload, params);
      if (attribution) payload.attribution = attribution;
      if (cta) payload.cta = cta;
      return payload;
    }
  }
  if (preset !== undefined) {
    Object.assign(payload, presetToParams(preset));
  }
  if (attribution) payload.attribution = attribution;
  if (cta) payload.cta = cta;
  return payload;
}

/**
 * Normalize the backend success payload into what the preview UI needs.
 * Tolerates the app's envelope shapes (raw, {data}, {data:{data}}). Throws a
 * clear Error if no usable video_url is present so the caller lands in ERROR.
 */
export function parseVideoResponse(raw) {
  const body = raw?.data?.data ?? raw?.data ?? raw ?? {};
  const url = body.video_url;
  if (!url || typeof url !== "string") {
    throw new Error("No video URL in response");
  }
  return {
    videoUrl: url,
    durationMs: Number(body.duration_ms) || 0,
    aspectRatio: isValidAspect(body.aspect_ratio) ? body.aspect_ratio : DEFAULT_ASPECT,
    sceneCount: Number(body.scene_count) || 0,
    isMock: Boolean(body.is_mock),
  };
}

/** Format ms → "M:SS" for the preview caption. */
export function formatDuration(ms) {
  const totalSec = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** A user-facing message for the error state, kept generic + non-leaky. */
export function errorMessage(err) {
  const msg = err?.response?.data?.message || err?.message;
  if (typeof msg === "string" && msg.trim() && msg.length < 140) return msg;
  return "Couldn't generate the video. Please try again.";
}

/**
 * The BE's `sub_tier` when a request was rejected by the share-video tier gate
 * (yuga_wheel/services/share_video_gating.py UpgradeRequiredError — a 403 whose
 * body carries `sub_tier`), or null for any other error.
 *
 * Distinguishes "retry might work" (network blip, transient 5xx) from "retrying
 * will 403 again forever" (the account's tier is below the required floor) so
 * the ERROR state can offer an Upgrade CTA instead of a dead Try-again loop.
 */
export function upgradeRequiredTier(err) {
  if (err?.response?.status !== 403) return null;
  const subTier = err?.response?.data?.sub_tier;
  return typeof subTier === "number" ? subTier : null;
}

// ── Async job lifecycle (C1+C12 — generate-share-video-async / share-video-status) ──

/**
 * Job lifecycle states as returned by the BE (yuga_wheel/models_share_video.py
 * ShareVideoStatus). Mirrored as string constants — never re-derived/guessed —
 * so a BE enum rename fails loudly (a typo'd string never matches).
 */
export const JobStatus = Object.freeze({
  QUEUED: "queued",
  RENDERING: "rendering",
  DONE: "done",
  FAILED: "failed",
});

/** True once a job has reached a terminal state — polling must stop here. */
export const isTerminalJobStatus = (status) =>
  status === JobStatus.DONE || status === JobStatus.FAILED;

/**
 * Normalize the enqueue/poll envelope into what the UI needs. Both
 * generate-share-video-async and share-video-status return the SAME
 * `_share_video_job_payload` shape server-side, so one parser covers both.
 * Tolerates the app's envelope shapes (raw, {data}, {data:{data}}) — same
 * convention as parseVideoResponse. Throws when job_id/status are absent so a
 * malformed response can't silently pass as a valid job.
 */
export function parseJobResponse(raw) {
  const body = raw?.data?.data ?? raw?.data ?? raw ?? {};
  if (body.job_id == null || !body.status) {
    throw new Error("No job in response");
  }
  return {
    jobId: body.job_id,
    status: body.status,
    videoUrl: body.video_url || null,
    durationMs: Number(body.duration_ms) || 0,
    aspectRatio: isValidAspect(body.aspect_ratio) ? body.aspect_ratio : DEFAULT_ASPECT,
    sceneCount: Number(body.scene_count) || 0,
    isMock: Boolean(body.is_mock),
    error: body.error || null,
    cacheHit: Boolean(body.cache_hit),
  };
}

/** Adapt a DONE job payload into the exact shape the PREVIEW state (result) expects. */
export function jobToVideoResult(job) {
  return {
    videoUrl: job.videoUrl,
    durationMs: job.durationMs,
    aspectRatio: job.aspectRatio,
    sceneCount: job.sceneCount,
    isMock: job.isMock,
  };
}
